
import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { trackGoogleAuth } from "@/utils/auth-tracking";
import { logAuthState } from "@/utils/debug-auth";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  loading: boolean;
  error: string | null;
  authStateReady: boolean;
  isEmailConfirmed: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  signIn: async () => {},
  signOut: async () => {},
  loading: true,
  error: null,
  authStateReady: false,
  isEmailConfirmed: false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authStateReady, setAuthStateReady] = useState(false);
  const [isEmailConfirmed, setIsEmailConfirmed] = useState(false);
  
  // Use a ref to track if we've initialized yet to prevent redundant loading states
  const initialized = useRef(false);
  
  // Add refs to hold the current state for comparison
  const currentSessionRef = useRef<Session | null>(null);
  const currentUserRef = useRef<User | null>(null);
  const isTabSwitchEvent = useRef(false);
  
  // Counter to track how many initialization attempts we've made
  const initializationAttempts = useRef(0);

  // Function to handle session state changes
  const handleSessionChange = (newSession: Session | null) => {
    // Only update state if the session has actually changed
    if (JSON.stringify(newSession) !== JSON.stringify(currentSessionRef.current)) {
      console.log("[AuthContext] Session changed, updating state");
      
      // Update refs
      currentSessionRef.current = newSession;
      currentUserRef.current = newSession?.user || null;
      
      // Update state
      setSession(newSession);
      setUser(newSession?.user || null);

      // Set email confirmation status based on session
      if (newSession?.user) {
        const isVerified = !!newSession.user.email_confirmed_at || 
                          newSession.user.app_metadata?.provider === 'google';
        setIsEmailConfirmed(isVerified);
        
        // Track successful auth if this is from Google
        if (newSession.user.app_metadata?.provider === 'google') {
          trackGoogleAuth('success', { 
            id: newSession.user.id,
            has_role: !!newSession.user.user_metadata?.role 
          });
        }
      } else {
        setIsEmailConfirmed(false);
      }
    }
  };

  useEffect(() => {
    async function getInitialSession() {
      try {
        // Only set loading to true on the initial load
        if (!initialized.current) {
          console.log('[AuthContext] Getting initial session...');
          setLoading(true);
        }
        
        // Check if we already have a session in localStorage but session object is null
        // This can happen due to race conditions with Google Auth
        const hasStoredSession = localStorage.getItem('testera_auth_token') !== null;
        console.log('[AuthContext] Found session in localStorage:', hasStoredSession);
        
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[AuthContext] Error getting session:', error);
          throw error;
        }
        
        console.log('[AuthContext] Initial session:', session ? 'exists' : 'none');
        if (session) {
          console.log('[AuthContext] Session user:', session.user.id);
          console.log('[AuthContext] Session metadata:', session.user.user_metadata);
          console.log('[AuthContext] Auth metadata:', session.user.app_metadata);
        } else if (hasStoredSession && initializationAttempts.current < 3) {
          // If we have a session in localStorage but no session object, try again
          console.log('[AuthContext] Session missing but localStorage has token, retrying in 1s...');
          initializationAttempts.current++;
          setTimeout(getInitialSession, 1000);
          return;
        }
        
        handleSessionChange(session);
      } catch (e: any) {
        console.error('[AuthContext] Unexpected error in getInitialSession:', e);
        setError(e.message);
      } finally {
        // Only set these flags once - they shouldn't flip back during tab switches
        if (!initialized.current) {
          setLoading(false);
          setAuthStateReady(true);
          initialized.current = true;
          console.log('[AuthContext] Auth state ready');
          
          // Debug auth state when ready
          setTimeout(logAuthState, 200);
        }
      }
    }

    getInitialSession();

    // Handle tab visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        isTabSwitchEvent.current = true;
        console.log('[AuthContext] Tab became visible, treating as tab switch event');
        // Reset the flag after a short delay
        setTimeout(() => {
          isTabSwitchEvent.current = false;
        }, 2000);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        // Log the event for debugging
        console.log('[AuthContext] Auth state changed, event:', event);
        console.log('[AuthContext] New session:', newSession ? 'exists' : 'none');
        if (newSession) {
          console.log('[AuthContext] New session user:', newSession.user.id);
          console.log('[AuthContext] New user metadata:', newSession.user.user_metadata);
          console.log('[AuthContext] Auth metadata:', newSession.user.app_metadata);
        }
        console.log('[AuthContext] Is tab switch event:', isTabSwitchEvent.current);
        
        // Process the session change
        handleSessionChange(newSession);
        
        // IMPORTANT: Don't reset authStateReady during tab switch events!
        // Only set it to true if it's not already set
        if (!authStateReady && !isTabSwitchEvent.current) {
          setAuthStateReady(true);
        }
        
        // Debug auth state on significant events
        if (['SIGNED_IN', 'SIGNED_OUT', 'USER_UPDATED', 'TOKEN_REFRESHED'].includes(event)) {
          setTimeout(logAuthState, 200);
        }
      }
    );

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      subscription.unsubscribe();
    };
  }, [authStateReady]);

  async function signIn(email: string, password: string) {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      
      currentSessionRef.current = data.session;
      currentUserRef.current = data.user;
      setSession(data.session);
      setUser(data.user);
      
      // Set email confirmation status
      if (data.user) {
        const isVerified = !!data.user.email_confirmed_at || 
                          data.user.app_metadata?.provider === 'google';
        setIsEmailConfirmed(isVerified);
      }
    } catch (e: any) {
      console.error('[AuthContext] Sign in error:', e);
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  }

  async function signOut() {
    try {
      setLoading(true);
      
      // Invalidate session tokens before signing out
      try {
        const { invalidateSessionTokens } = await import('@/utils/webcam-storage');
        await invalidateSessionTokens();
      } catch (importError) {
        console.warn('Could not invalidate session tokens:', importError);
      }
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      currentSessionRef.current = null;
      currentUserRef.current = null;
      setSession(null);
      setUser(null);
      setIsEmailConfirmed(false);
    } catch (e: any) {
      console.error('[AuthContext] Sign out error:', e);
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        signIn,
        signOut,
        loading,
        error,
        authStateReady,
        isEmailConfirmed,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
